import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Settings, Droplet, Bell, Plus, Minus, Scale, Save, Lock, LogOut, Star, User as UserIcon, Flame, Copy, Key, Camera, Trash2 } from "lucide-react";
import { api, BACKEND_URL } from "../api/client";
import { useLanguage } from "../context/LanguageContext";

const mediaSrc = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

const SettingsView = ({ user, token, onThemeChange, onUserUpdate, gamification, onLogout }) => {
  const { lang, setLang } = useLanguage();
  const [preferences, setPreferences] = useState({
    theme: "dark",
    hydration_goal: 2000,
    water_reminder_enabled: false,
    water_reminder_interval: 60
  });
  const [profile, setProfile] = useState({ name: user.name, email: user.email });
  const [measurements, setMeasurements] = useState([]);
  const [newMeasurement, setNewMeasurement] = useState({ weight: "", waist: "", chest: "", hips: "", date: "" });
  const [measurementOpen, setMeasurementOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", newPass: "", confirm: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url || null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarFileRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prefsRes, measurementsRes] = await Promise.all([
        api("get", "/user/preferences", null, token),
        // Ten ekran pokazuje wyłącznie ostatni pomiar, więc pobiera właśnie
        // jego, a nie całą historię. `/measurements/latest` istniał od dawna
        // i nie miał żadnego wywołania.
        api("get", "/measurements/latest", null, token).catch(() => ({ data: null }))
      ]);
      setPreferences({
        theme: prefsRes.data?.theme || "dark",
        hydration_goal: prefsRes.data?.hydration_goal || 2000,
        water_reminder_enabled: prefsRes.data?.water_reminder_enabled || false,
        water_reminder_interval: prefsRes.data?.water_reminder_interval || 60
      });
      setMeasurements(measurementsRes.data ? [measurementsRes.data] : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateTheme = async (theme) => {
    setPreferences(prev => ({ ...prev, theme }));
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("duofit-theme", theme);

    try {
      await api("put", "/user/preferences", { ...preferences, theme }, token);
      if (onThemeChange) onThemeChange(theme);
    } catch (err) {
      console.error(err);
    }
  };

  const updateHydrationGoal = async (goal) => {
    const newGoal = Math.max(500, Math.min(5000, goal));
    setPreferences(prev => ({ ...prev, hydration_goal: newGoal }));

    try {
      await api("put", "/user/preferences", { ...preferences, hydration_goal: newGoal }, token);
      toast.success("Cel nawodnienia zaktualizowany");
    } catch (err) {
      console.error(err);
    }
  };

  const toggleWaterReminder = async () => {
    const newEnabled = !preferences.water_reminder_enabled;
    setPreferences(prev => ({ ...prev, water_reminder_enabled: newEnabled }));

    try {
      await api("put", "/user/preferences", { ...preferences, water_reminder_enabled: newEnabled }, token);
      toast.success(newEnabled ? "Przypomnienia włączone" : "Przypomnienia wyłączone");
    } catch (err) {
      console.error(err);
    }
  };

  const updateReminderInterval = async (interval) => {
    const newInterval = Math.max(15, Math.min(180, interval));
    setPreferences(prev => ({ ...prev, water_reminder_interval: newInterval }));

    try {
      await api("put", "/user/preferences", { ...preferences, water_reminder_interval: newInterval }, token);
    } catch (err) {
      console.error(err);
    }
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Wybierz plik graficzny"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Zdjęcie za duże (max 5MB)"); return; }
    setUploadingAvatar(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api("post", "/user/avatar", form, token);
      setAvatarUrl(res.data.avatar_url);
      if (onUserUpdate) onUserUpdate({ avatar_url: res.data.avatar_url });
      toast.success("Zdjęcie zaktualizowane");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się przesłać zdjęcia");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const removeAvatar = async () => {
    try {
      await api("delete", "/user/avatar", null, token);
      setAvatarUrl(null);
      if (onUserUpdate) onUserUpdate({ avatar_url: null });
      toast.success("Zdjęcie usunięte");
    } catch {
      toast.error("Nie udało się usunąć zdjęcia");
    }
  };

  const updateProfile = async () => {
    try {
      const res = await api("put", "/user/profile", profile, token);
      toast.success("Profil zaktualizowany");
      if (onUserUpdate) onUserUpdate(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zapisać");
    }
  };

  const saveMeasurement = async () => {
    try {
      await api("post", "/measurements", {
        weight: newMeasurement.weight ? parseFloat(newMeasurement.weight) : null,
        waist: newMeasurement.waist ? parseFloat(newMeasurement.waist) : null,
        chest: newMeasurement.chest ? parseFloat(newMeasurement.chest) : null,
        hips: newMeasurement.hips ? parseFloat(newMeasurement.hips) : null,
        date: newMeasurement.date || null
      }, token);
      toast.success("Pomiary zapisane");
      setMeasurementOpen(false);
      setNewMeasurement({ weight: "", waist: "", chest: "", hips: "", date: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const THEMES = [
    { id: "dark", name: "Ciemny" },
    { id: "light", name: "Jasny" },
    { id: "ocean", name: "Ocean" },
    { id: "forest", name: "Las" },
    { id: "sunset", name: "Zachód" },
    { id: "candy", name: "Candy" },
  ];

  if (loading) return <div className="loading"><Settings className="spin" size={32} /></div>;

  const level = gamification?.level || 1;
  const xpPct = gamification?.xp_progress_percent || 0;
  const streak = gamification?.workout_streak || 0;
  const xp = gamification?.xp || 0;
  const xpNext = xp + (gamification?.xp_for_next_level || 100);

  return (
    <>
      {/* Profile Hero Card */}
      <section className="profile-hero-card">
        <div className="profile-hero-avatar" style={{ position: "relative" }}>
          <svg width={100} height={100} viewBox="0 0 100 100">
            <defs>
              <linearGradient id="profile-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#CEFF00" />
                <stop offset="100%" stopColor="#FF9500" />
              </linearGradient>
            </defs>
            <circle cx={50} cy={50} r={44} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={6} />
            <circle cx={50} cy={50} r={44} fill="none" stroke="url(#profile-ring-grad)" strokeWidth={6}
              strokeDasharray={2 * Math.PI * 44}
              strokeDashoffset={2 * Math.PI * 44 - (xpPct / 100) * 2 * Math.PI * 44}
              strokeLinecap="round" transform="rotate(-90 50 50)" />
          </svg>
          <div className="profile-hero-icon" style={{ overflow: "hidden" }}>
            {avatarUrl
              ? <img src={mediaSrc(avatarUrl)} alt="Avatar" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
              : <UserIcon size={36} />}
          </div>
          <button type="button" onClick={() => avatarFileRef.current?.click()} disabled={uploadingAvatar}
            aria-label="Zmień zdjęcie profilowe" title="Zmień zdjęcie profilowe"
            style={{
              position: "absolute", right: -2, bottom: -2, width: 32, height: 32, borderRadius: "50%",
              border: "2px solid var(--bg, #111)", cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff",
            }}>
            <Camera size={15} />
          </button>
          {avatarUrl && (
            <button type="button" onClick={removeAvatar} aria-label="Usuń zdjęcie" title="Usuń zdjęcie"
              style={{
                position: "absolute", left: -2, bottom: -2, width: 28, height: 28, borderRadius: "50%",
                border: "2px solid var(--bg, #111)", cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", background: "rgba(0,0,0,0.6)", color: "#fff",
              }}>
              <Trash2 size={13} />
            </button>
          )}
          <input ref={avatarFileRef} type="file" accept="image/*" onChange={onPickAvatar} style={{ display: "none" }} />
        </div>
        <div className="profile-hero-level-badge">
          <Star size={12} /> Lvl {level}
        </div>
        <h2 className="profile-hero-name">{user.name}</h2>
        {streak > 0 && (
          <div className="profile-hero-streak">
            <Flame size={14} /> {streak} dni z rzedu
          </div>
        )}
        <span className="profile-hero-xp">{xp} / {xpNext} XP</span>
      </section>

      {/* Trainer code / connection */}
      {(user.role === "client" || user.role === "trainer") && (
        <section className="settings-section">
          <Card className="setting-card">
            <CardContent>
              <div className="setting-row">
                <div className="setting-info">
                  <Key size={20} />
                  <div>
                    <h3>{user.role === "trainer" ? "Twoj kod trenera" : user.trainer_id ? "Polaczony z trenerem" : "Brak trenera"}</h3>
                    <p>{user.role === "trainer"
                      ? "Udostepnij klientom aby sie polaczyli"
                      : user.trainer_id ? "Masz przypisanego trenera" : "Uzyj kodu aby sie polaczyc"}</p>
                  </div>
                </div>
                {(user.trainer_code) && (
                  <button className="trainer-code-copy" onClick={() => {
                    navigator.clipboard.writeText(user.trainer_code);
                    toast.success("Skopiowano kod");
                  }} style={{
                    display:'flex', alignItems:'center', gap:'8px', padding:'8px 14px',
                    borderRadius:'8px', border:'1px solid rgba(255,255,255,0.1)',
                    background:'rgba(255,255,255,0.03)', cursor:'pointer', color:'#fff'
                  }}>
                    <code style={{fontFamily:'monospace', fontSize:'0.9rem', fontWeight:600, letterSpacing:'0.05em'}}>
                      {user.trainer_code}
                    </code>
                    <Copy size={14} style={{color:'var(--text-secondary)'}} />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <section className="settings-section">
        <h2>Kolorystyka</h2>
        <div className="theme-grid" data-testid="theme-grid">
          {THEMES.map(theme => (
            <button
              key={theme.id}
              className={`theme-card ${preferences.theme === theme.id ? 'active' : ''}`}
              onClick={() => updateTheme(theme.id)}
              data-testid={`theme-card-${theme.id}`}
            >
              <div className={`theme-preview ${theme.id}`}></div>
              <span>{theme.name}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="settings-section">
        <h2>Język / Language</h2>
        <div className="theme-grid">
          {[
            { id: "pl", name: "Polski 🇵🇱" },
            { id: "en", name: "English 🇬🇧" }
          ].map(l => (
            <button
              key={l.id}
              className={`theme-card ${lang === l.id ? 'active' : ''}`}
              onClick={() => setLang(l.id)}
            >
              <span style={{fontSize:20}}>{l.id === 'pl' ? '🇵🇱' : '🇬🇧'}</span>
              <span>{l.name}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="settings-section">
        <h2>Nawodnienie</h2>
        <Card className="setting-card">
          <CardContent>
            <div className="setting-row">
              <div className="setting-info">
                <Droplet size={20} />
                <div>
                  <h3>Cel dzienny</h3>
                  <p>Ile wody chcesz pić dziennie</p>
                </div>
              </div>
              <div className="goal-adjuster">
                <Button variant="outline" size="sm" onClick={() => updateHydrationGoal(preferences.hydration_goal - 250)}>
                  <Minus size={14} />
                </Button>
                <span className="goal-value">{(preferences.hydration_goal / 1000).toFixed(1)}L</span>
                <Button variant="outline" size="sm" onClick={() => updateHydrationGoal(preferences.hydration_goal + 250)}>
                  <Plus size={14} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="setting-card">
          <CardContent>
            <div className="setting-row">
              <div className="setting-info">
                <Bell size={20} />
                <div>
                  <h3>Przypomnienia</h3>
                  <p>Co {preferences.water_reminder_interval} min</p>
                </div>
              </div>
              <div className="reminder-controls">
                {preferences.water_reminder_enabled && (
                  <div className="interval-adjuster">
                    <Button variant="outline" size="sm" onClick={() => updateReminderInterval(preferences.water_reminder_interval - 15)}>
                      <Minus size={14} />
                    </Button>
                    <span>{preferences.water_reminder_interval}m</span>
                    <Button variant="outline" size="sm" onClick={() => updateReminderInterval(preferences.water_reminder_interval + 15)}>
                      <Plus size={14} />
                    </Button>
                  </div>
                )}
                <Button
                  variant={preferences.water_reminder_enabled ? "default" : "outline"}
                  size="sm"
                  onClick={toggleWaterReminder}
                  data-testid="toggle-reminder"
                >
                  {preferences.water_reminder_enabled ? "Wł" : "Wył"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="settings-section">
        <h2>Pomiary</h2>
        <Card className="setting-card">
          <CardContent>
            <div className="setting-row">
              <div className="setting-info">
                <Scale size={20} />
                <div>
                  <h3>Ostatni pomiar</h3>
                  {measurements.length > 0 ? (
                    <p>
                      {measurements[0].weight && `${measurements[0].weight} kg`}
                      {measurements[0].waist && ` • Talia: ${measurements[0].waist} cm`}
                    </p>
                  ) : (
                    <p>Brak pomiarów</p>
                  )}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setMeasurementOpen(true)} data-testid="add-measurement">
                <Plus size={14} /> Dodaj
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="settings-section">
        <h2>Konto</h2>
        <Card className="setting-card">
          <CardContent>
            <div className="profile-form">
              <div className="field">
                <Label>Imię</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile({...profile, name: e.target.value})}
                  data-testid="profile-name"
                />
              </div>
              <div className="field">
                <Label>Email</Label>
                <Input
                  value={profile.email}
                  onChange={(e) => setProfile({...profile, email: e.target.value})}
                  data-testid="profile-email"
                />
              </div>
              <Button onClick={updateProfile} data-testid="save-profile">
                <Save size={16} /> Zapisz zmiany
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="settings-section">
        <h2>Zmiana hasła</h2>
        <Card className="setting-card">
          <CardContent>
            <div className="profile-form">
              <div className="field">
                <Label>Aktualne hasło</Label>
                <Input
                  type="password"
                  value={passwordForm.current}
                  onChange={e => setPasswordForm({...passwordForm, current: e.target.value})}
                />
              </div>
              <div className="field">
                <Label>Nowe hasło</Label>
                <Input
                  type="password"
                  value={passwordForm.newPass}
                  onChange={e => setPasswordForm({...passwordForm, newPass: e.target.value})}
                />
              </div>
              <div className="field">
                <Label>Powtórz nowe hasło</Label>
                <Input
                  type="password"
                  value={passwordForm.confirm}
                  onChange={e => setPasswordForm({...passwordForm, confirm: e.target.value})}
                />
              </div>
              <Button
                disabled={changingPassword || !passwordForm.current || !passwordForm.newPass}
                onClick={async () => {
                  if (passwordForm.newPass.length < 6) { toast.error("Hasło musi mieć minimum 6 znaków"); return; }
                  if (passwordForm.newPass !== passwordForm.confirm) { toast.error("Hasła nie są identyczne"); return; }
                  setChangingPassword(true);
                  try {
                    await api("put", "/auth/change-password", {
                      current_password: passwordForm.current,
                      new_password: passwordForm.newPass
                    }, token);
                    toast.success("Hasło zmienione");
                    setPasswordForm({ current: "", newPass: "", confirm: "" });
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Nie udało się zmienić hasła");
                  } finally {
                    setChangingPassword(false);
                  }
                }}
              >
                <Lock size={16} /> {changingPassword ? "Zmieniam..." : "Zmień hasło"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Logout */}
      {onLogout && (
        <section className="settings-section">
          <Button variant="outline" className="logout-btn-full" onClick={onLogout} data-testid="logout-btn">
            <LogOut size={16} /> Wyloguj sie
          </Button>
        </section>
      )}

      {/* Measurement Dialog */}
      <Dialog open={measurementOpen} onOpenChange={setMeasurementOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nowy pomiar</DialogTitle>
            <DialogDescription>Wprowadź swoje pomiary</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Data pomiaru</Label>
              <Input
                type="date"
                value={newMeasurement.date}
                onChange={(e) => setNewMeasurement({...newMeasurement, date: e.target.value})}
                max={new Date().toISOString().split('T')[0]}
              />
              <span className="field-hint">Puste = dzisiaj</span>
            </div>
            <div className="measurement-grid">
              <div className="field">
                <Label>Waga (kg)</Label>
                <Input
                  type="number"
                  value={newMeasurement.weight}
                  onChange={(e) => setNewMeasurement({...newMeasurement, weight: e.target.value})}
                  placeholder="75.5"
                />
              </div>
              <div className="field">
                <Label>Talia (cm)</Label>
                <Input
                  type="number"
                  value={newMeasurement.waist}
                  onChange={(e) => setNewMeasurement({...newMeasurement, waist: e.target.value})}
                  placeholder="80"
                />
              </div>
              <div className="field">
                <Label>Klatka (cm)</Label>
                <Input
                  type="number"
                  value={newMeasurement.chest}
                  onChange={(e) => setNewMeasurement({...newMeasurement, chest: e.target.value})}
                  placeholder="100"
                />
              </div>
              <div className="field">
                <Label>Biodra (cm)</Label>
                <Input
                  type="number"
                  value={newMeasurement.hips}
                  onChange={(e) => setNewMeasurement({...newMeasurement, hips: e.target.value})}
                  placeholder="95"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeasurementOpen(false)}>Anuluj</Button>
            <Button onClick={saveMeasurement}>Zapisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SettingsView;
